/* Resolve the URL to a route, enforce sign-in, shell and permission, render the screen. */
import { Suspense, useEffect } from 'react';
import { Link, matchPath, navigate, useRouter } from './router';
import { routes, type RouteDef } from './routes';
import { homeFor, useSession } from './session';
import { NotAllowed, PortalShell, PublicFrame, StaffShell } from '../shells/shells';
import { Loading } from '../components/ui';


function resolve(pathname: string): { route: RouteDef; params: Record<string, string> } | null {
  for (const r of routes) {
    const params = matchPath(r.path, pathname);
    if (params) return { route: r, params };
  }
  return null;
}

export function useParams(): Record<string, string> {
  const { loc } = useRouter();
  return resolve(loc.pathname)?.params ?? {};
}

function Redirect({ to }: { to: string }) {
  useEffect(() => { navigate(to, { replace: true }); }, [to]);
  return <Loading />;
}

export function App() {
  const { loc } = useRouter();
  const { me, loading, can } = useSession();
  const hit = resolve(loc.pathname);

  useEffect(() => {
    document.title = hit ? `${hit.route.title} · ACGCET Hostel` : 'ACGCET Hostel';
  }, [hit]);

  if (loading) return <Loading label="Starting…" />;
  if (loc.pathname === '/') return <Redirect to={homeFor(me)} />;
  if (!hit) {
    return (
      <PublicFrame>
        <div className="card card-body"><h2>Page not found</h2><p className="muted mt">The address {loc.pathname} does not exist.</p>
          <div className="mt"><Link to="/">Go home</Link></div></div>
      </PublicFrame>
    );
  }
  const { route } = hit;
  const Screen = route.component;
  const body = <Suspense fallback={<Loading />}><Screen /></Suspense>;

  if (route.shell === 'auth') return body;
  if (route.shell === 'public') return <PublicFrame>{body}</PublicFrame>;
  if (!me) return <Redirect to={route.shell === 'staff' ? `/login?next=${encodeURIComponent(loc.pathname + loc.search)}` : '/portal/login'} />;

  if (route.shell === 'staff') {
    if (me.user_type !== 'staff') return <Redirect to={homeFor(me)} />;
    if (me.mfa_required && !me.mfa_verified) return <Redirect to="/login/2fa" />;
    return <StaffShell>{route.perm && !can(...route.perm) ? <NotAllowed /> : body}</StaffShell>;
  }
  if (route.shell === 'student') {
    if (me.user_type !== 'student') return <Redirect to={homeFor(me)} />;
    return <PortalShell kind="student">{body}</PortalShell>;
  }
  if (me.user_type !== 'parent') return <Redirect to={homeFor(me)} />;
  return <PortalShell kind="parent">{body}</PortalShell>;
}
